import { getField } from 'app/components/SearchFilter/Constants';
import { MlsListingStatus } from './ConstantsNew';

// Real left-panel category key (matches ConstantsNew.js's
// leftPanelCategories: ll/pb/pi/mls/pfbo/oio/lbd/ve/m) for each of the 96
// Filter Taxonomy fields, keyed by base name (Min/Max suffix stripped).
// Neither getField()'s FilterFieldMap nor ConstantsNew.js's own
// FilterSectionsNew carries a usable category reference per field (the one
// place Constants.js reads `field.section` is dead code — no field literal
// ever sets that property), so this has to be its own hand-built map,
// sourced from the Confluence "Filter Taxonomy — AI Search (by category)"
// page. Lets AI-applied filters land in the SAME real category the manual
// UI already uses, rather than a fabricated "AI Search" bucket.
const FIELD_CATEGORY_KEYS = {
  // Lead Lists
  listingType: 'll',
  // Property Details
  propertyClassCode: 'pb',
  residentialPropCode: 'pb',
  multiFamilyPropCode: 'pb',
  commercialPropCode: 'pb',
  vacantPropCode: 'pb',
  officePropCode: 'pb',
  industrialPropCode: 'pb',
  agriculturalPropCode: 'pb',
  recreationalPropCode: 'pb',
  transportationPropCode: 'pb',
  exemptPropCode: 'pb',
  bedrooms: 'pb',
  bathrooms: 'pb',
  squareFeet: 'pb',
  lotSquareFeet: 'pb',
  yearBuilt: 'pb',
  schoolDistrict: 'pb',
  units: 'pb',
  stories: 'pb',
  hoaPresent: 'pb',
  poolAvailable: 'pb',
  poolType: 'pb',
  garageAvailable: 'pb',
  garageType: 'pb',
  basementAvailable: 'pb',
  basementType: 'pb',
  atticAvailable: 'pb',
  // PropStream Intelligence
  propCondition: 'pi',
  bathCondition: 'pi',
  kitchenCondition: 'pi',
  intCondition: 'pi',
  extCondition: 'pi',
  foreclosureFactor: 'pi',
  // MLS
  forSale: 'mls',
  rental: 'mls',
  mlsListingStatus: 'mls',
  mlsListingDate: 'mls',
  daysOnMarket: 'mls',
  mlsListingAmount: 'mls',
  belowMarketPrice: 'mls',
  mlsListingKeyword: 'mls',
  // Pre-Foreclosure & Bank Owned
  foreclosureStatus2Class: 'pfbo',
  foreclosureStatus2: 'pfbo',
  foreclosureRecordingDate: 'pfbo',
  auctionDate: 'pfbo',
  foreclosureReleaseDate: 'pfbo',
  openingBidAmount: 'pfbo',
  defaultAmount: 'pfbo',
  // Owner Information & Occupancy
  ownerOccupied: 'oio',
  vacant: 'oio',
  ownershipLength: 'oio',
  ownerType: 'oio',
  ownerLocation: 'oio',
  saleAmount: 'oio',
  propertiesOwned: 'oio',
  saleDate: 'oio',
  missingSaleIncluded: 'oio',
  ownerDeceased: 'oio',
  interFamilyTransfer: 'oio',
  exemptions: 'oio',
  // Lien, Bankruptcy & Divorce
  lienActive: 'lbd',
  lienTypeCode: 'lbd',
  lienRecordingDate: 'lbd',
  lienAmount: 'lbd',
  bankruptcyActive: 'lbd',
  bankruptcyChapter: 'lbd',
  bankruptcyRecordingDate: 'lbd',
  divorceActive: 'lbd',
  divorceRecordingDate: 'lbd',
  delinquentTaxYear: 'lbd',
  // Value & Equity
  estimatedValue: 've',
  assessedLandValue: 've',
  estimatedValueGrowth: 've',
  wholesaleValue: 've',
  estimatedRentalIncome: 've',
  grossYield: 've',
  estimatedEquity: 've',
  estimatedEquityPercent: 've',
  loanToValueRatio: 've',
  assessedValue: 've',
  assessedImprovementValue: 've',
  itvRatio: 've',
  unknownEquityIncluded: 've',
  // Mortgage
  mortgageCount: 'm',
  openMortgageBalance: 'm',
  mortgageRecDate: 'm',
  mortgageLoanCode: 'm',
  mortgageTotalPayment: 'm',
  mortgageInterestRate: 'm',
  mortgageFinancingCode: 'm',
  mortgage1LtvRatio: 'm',
  mortgage1EstimatedEquity: 'm',
  cashBuyer: 'm',
  freeClear: 'm',
  mortgageSellerCarried: 'm',
};

// The real category key a filter belongs to (matches ConstantsNew.js's
// leftPanelCategories, e.g. 'oio' for Owner Information & Occupancy) — so
// an AI-applied filter's localFilter record lands in the same left-panel
// section a manually-applied one would, not a separate bucket. Falls back
// to 'pb' (Property Details) for anything not in the map — should not
// happen for a field that passed validateFilterValue, but a badge miscount
// is a much smaller problem than crashing on an undefined section.
export function filterCategoryKey(key) {
  const base = /^(.*)(Min|Max)$/.exec(key)?.[1] || key;
  return FIELD_CATEGORY_KEYS[base] || 'pb';
}

// camelCase filter key -> "Camel Case". Fallback only, for a field
// getField() doesn't cover (e.g. lienActive, modeled there as
// lienTypeCode's includeKey rather than its own entry).
function humanizeFilterKey(key) {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Reuses the legacy SearchFilter UI's own live field-label map
// (src/app/components/SearchFilter/Constants.js's FilterFieldMap/getField) —
// ~93 real field-name -> label entries, the same ones Viewer.js/ViewerNew.js
// use to render a saved search's filter summary. Not the redesigned
// searchFilterNew's ConstantsNew.js: that file's own FilterSectionsNew is
// ~98% commented-out dead code, covering only 2 of the 96 taxonomy fields.
// getField() already strips a trailing Min/Max suffix to look up the base
// field, so both halves of a range share one label; re-append the suffix
// here so a chip still reads e.g. "Bedrooms Min" rather than just "Bedrooms".
export function filterFieldLabel(key) {
  const field = getField(key);
  const label = field?.label || humanizeFilterKey(key);
  if (/Min$/.test(key)) return `${label} Min`;
  if (/Max$/.test(key)) return `${label} Max`;
  return label;
}

// Only these two boolean fields have a *generic group* field.label
// ("Occupancy Status" for vacant, "Listing Type" for rental) that doesn't
// itself say what true/false means — the specific meaning lives in the
// field's own `options` list instead (getField('vacant').options:
// 'true'->'Vacant', 'false'->'Occupied'; getField('rental').options:
// 'true'->'For Rent', 'false'->'For Sale'). NOT a blanket rule for every
// boolean+options field — most others (cashBuyer, freeClear, hoaPresent,
// ownerDeceased, interFamilyTransfer, mortgageSellerCarried) share a
// generic Include/Exclude options pair, and resolving through THAT would
// produce a worse, context-free chip ("Include") than their already-
// meaningful bare field label ("Cash Buyer").
const VALUE_LABEL_FIELDS = new Set(['vacant', 'rental']);

// Reads options off the live field descriptor rather than importing
// OccupancyOptions/MlsListingTypeOptions directly — those are private to
// Constants.js, not exported.
export function filterValueLabel(key, value) {
  if (typeof value !== 'boolean' || !VALUE_LABEL_FIELDS.has(key)) return null;
  const field = getField(key);
  const option = field?.options?.find((o) => o.value === String(value));
  return option?.label || null;
}

// getField()'s option lists come from the legacy SearchFilter UI and are in
// places a strict SUBSET of what the AI can actually apply: getField(
// 'mlsListingStatus').options is data/search's 6-entry MlsListingStatus
// (Active/Pending/Contingent/Failed/Sold/Never Listed), while the redesigned
// UI's own ConstantsNew list carries all 13 (ACTIVE_UNDER_CONTRACT,
// COMING_SOON, CANCELED, EXPIRED, REMOVED, WITHDRAWN, DELETED on top). Nothing
// upstream closes that gap — validateFilterValue keeps every string for a
// 'list' field without checking option membership — so the extra codes reached
// the Applied Filter Summary card raw ("MLS Status: ACTIVE,
// ACTIVE_UNDER_CONTRACT, COMING_SOON, ..." — ATAI-220). Consulted only after
// getField's own list misses; one entry today, keyed by filter name so a
// second stale list is a one-line addition.
const EXTRA_OPTION_SOURCES = {
  mlsListingStatus: MlsListingStatus,
};

// The two option shapes this has to read are inverted, so match and display
// can't both key off the same property:
//   data/search, data/property  { label: 'Active',  value: 'ACTIVE' }
//   ConstantsNew                { label: 'ACTIVE', name: 'Active', id: 'ACTIVE' }
// Match on `value ?? id`, display `name ?? label` — correct for both, rather
// than reading `label` (which is the CODE in the ConstantsNew shape).
function optionCode(option) {
  return option.value != null ? option.value : option.id;
}

function optionDisplay(option) {
  return option.name != null ? option.name : option.label;
}

// SENIOR_CITIZEN -> "Senior Citizen". Last resort for a code that appears in
// no list at all: a stale catalog then degrades to a readable label instead
// of putting a raw enum on screen the way this bug did.
function humanizeFilterCode(code) {
  return code
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// One display label per raw value of a multi-select ('list') filter, resolved
// through getField's options first, then EXTRA_OPTION_SOURCES, then humanized.
// Returns an array rather than a joined string so a caller can render a chip
// per value (FilterSummaryCard) or join them itself (the localFilter chip
// records) — the two callers need different shapes of the same resolution.
export function filterValueLabels(key, values) {
  const list = Array.isArray(values) ? values : [values];
  const options = [...(getField(key)?.options || []), ...(EXTRA_OPTION_SOURCES[key] || [])];
  return list.map((value) => {
    const code = String(value);
    const option = options.find((o) => String(optionCode(o)) === code);
    if (option) {
      // known code — the catalog's own display label
      return optionDisplay(option);
    }
    // unknown code (stale/partial catalog) — readable fallback, never raw
    return humanizeFilterCode(code);
  });
}

// A multi-select ('list') field, whose value is a set of enum codes needing
// label resolution — true even when the model sends a single code as a bare
// string rather than a one-element array. validateFilterValue doesn't coerce
// a string to an array, so keying purely off Array.isArray left that path
// rendering the raw code ("MLS Status: ACTIVE" — PR #3461 review, nit 3).
export function isListFilter(key) {
  return getField(key)?.type === 'list';
}
